// src/lib/supabase/queries/share-queries.ts
/**
 * .tuMetrics {"tu1": 0.85, "tu2": 0.87, "tu3": 5, "notes": "Shared-item CRUD queries + DB->app mapping; filters ShareFinder on save_to_share_finder", "tuVersion": "2.5"}
 * .help "Fetches, inserts, updates, and deletes shared items for streams and ShareFinder."
 */
import { supabase } from '../../supabase';
import type { Database } from '../../../types/supabase';
import type { SharedItem } from '../../../types';

type SharedItemInsert = Database['public']['Tables']['shared_items']['Insert'];
type SharedItemUpdate = Database['public']['Tables']['shared_items']['Update'];

export const mapSharedItemFromDB = (row: any): SharedItem => {
  // Handle joined url_metadata (can be object or array)
  let urlMetadata = null;
  if (row.url_metadata) {
    // If it's an array (from join), take the first item
    if (Array.isArray(row.url_metadata) && row.url_metadata.length > 0) {
      urlMetadata = row.url_metadata[0];
    } else if (typeof row.url_metadata === 'object') {
      urlMetadata = row.url_metadata;
    }
  }
  
  // Handle joined drive_file_metadata (November 19, 2025)
  let driveFileMetadata = null;
  if (row.drive_file_metadata) {
    // If it's an array (from join), take the first item
    if (Array.isArray(row.drive_file_metadata) && row.drive_file_metadata.length > 0) {
      driveFileMetadata = row.drive_file_metadata[0];
    } else if (typeof row.drive_file_metadata === 'object') {
      driveFileMetadata = row.drive_file_metadata;
    }
  }
  
  return {
    id: row.id,
    title: row.title,
    description: row.description || '',
    url: row.url,
    type: row.type as SharedItem['type'],
    companyId: row.company_id,
    createdById: row.created_by_id,
    createdAt: row.created_at,
    tags: row.tags || [],
    cardId: row.card_id || undefined,
    requirementId: row.requirement_id || undefined, // For linking attachments to Features/Requirements
    teamId: row.team_id || undefined,
    // URL metadata fields (October 29, 2025)
    url_metadata_id: row.url_metadata_id || undefined,
    urlMetadata: urlMetadata || undefined,
    metadata_extracted: row.metadata_extracted || false,
    metadata_extraction_failed: row.metadata_extraction_failed || false,
    // Drive file metadata fields (November 19, 2025)
    drive_file_metadata_id: row.drive_file_metadata_id || undefined,
    driveFileMetadata: driveFileMetadata || undefined,
    // June 1, 2026: Whether this item is catalogued in ShareFinder (vs stream-only)
    saveToShareFinder: row.save_to_share_finder ?? true,
  };
};

// NEW: Function to fetch shared items (October 29, 2025: includes url_metadata join)
// ============================================
// PERFORMANCE OPTIMIZATION: Pagination Support
// ============================================
// Strategy: Support optional pagination (50 items initially) for Share Finder.
// ============================================
export const fetchSharedItems = async (
  companyId: string,
  options?: { limit?: number; offset?: number }
) => {
  let query = supabase
    .from('shared_items')
    .select(`
      *,
      shared_item_recipients!left(recipient_type, recipient_id),
      url_metadata(*),
      drive_file_metadata(*)
    `)
    .eq('company_id', companyId)
    // June 1, 2026: ShareFinder library only shows items the sharer opted to save.
    // Items shared stream-only (save_to_share_finder = false) still route to recipients
    // but are excluded from the ShareFinder catalogue here.
    .eq('save_to_share_finder', true)
    .order('created_at', { ascending: false });

  // PERFORMANCE: Apply pagination if provided
  if (options?.limit) {
    query = query.limit(options.limit);
  }
  if (options?.offset) {
    query = query.range(options.offset, (options.offset + (options.limit || 0)) - 1);
  }

  const { data, error } = await query;

  if (error) throw error;
  return data;
};

// NEW: Function to insert a shared item
export const insertSharedItem = async (itemData: SharedItemInsert) => {
  const { data, error } = await supabase
    .from('shared_items')
    .insert(itemData)
    .select()
    .single();

  if (error) throw error;
  return data;
};

// NEW: Function to update a shared item
// December 23, 2025: Include metadata joins to preserve rich preview data
export const updateSharedItemById = async (sharedItemId: string, updates: SharedItemUpdate) => {
  const { data, error } = await supabase
    .from('shared_items')
    .update(updates)
    .eq('id', sharedItemId)
    .select(`
      *,
      shared_item_recipients!left(recipient_type, recipient_id),
      url_metadata(*),
      drive_file_metadata(*)
    `)
    .single();

  if (error) throw error;
  return data;
};

// NEW: Function to fetch a single shared item by ID (December 23, 2025: For editing from stream)
export const fetchSharedItemById = async (sharedItemId: string) => {
  const { data, error } = await supabase
    .from('shared_items')
    .select(`
      *,
      shared_item_recipients!left(recipient_type, recipient_id),
      url_metadata(*),
      drive_file_metadata(*)
    `)
    .eq('id', sharedItemId)
    .single();

  if (error) throw error;
  return data;
};

// NEW: Function to delete a shared item
export const deleteSharedItemById = async (sharedItemId: string) => {
  const { error } = await supabase
    .from('shared_items')
    .delete()
    .eq('id', sharedItemId);

  if (error) throw error;
  return true;
};
